import React, { useState, useRef } from "react"
import { Button, OutlineButton } from "../shared/Button"
import Cropper from "react-cropper"
import { CardImage } from "react-bootstrap-icons"
import { isMobile } from "react-device-detect"
import { toast } from "react-toastify"
import { convertHeicToJpeg } from "../../util/heicConversion"

export const EventFlyerPhotoUploadSection = ({
  setOriginalFile,
  setCropper,
  setGifFile,
  gifPreview,
  coverImageUrl,
  onRemoveCoverImage,
  isGeneratedCover,
  setIsProcessingPhoto,
}) => {
  const [btnActive, setBtnActive] = useState(true)
  const [uploadedFilePath, setUploadedFilePath] = useState("")
  const [removeImage, setRemoveImage] = useState(false)
  const [coverRemoved, setCoverRemoved] = useState(false)
  const cropperRef = useRef(null)

  const hiddenFileInput = useRef(null)
  const handleClick = (event) => {
    hiddenFileInput.current.click()
  }

  const reader = new FileReader()
  reader.onload = (e) => {
    // @ts-ignore
    setUploadedFilePath(e.target.result)
  }

  const onUploadFlyerPhoto = async (attachment) => {
    setBtnActive(false)
    setIsProcessingPhoto?.(true)
    try {
      const file = await convertHeicToJpeg(attachment)
      setOriginalFile(file)
      if (file.type !== "image/gif") {
        // isProcessingPhoto stays true until the Cropper below actually
        // initializes (onInitialized) - GIFs never get a cropper, so they
        // clear it immediately instead.
        reader.readAsDataURL(file)
      } else {
        setIsProcessingPhoto?.(false)
      }
    } catch (e) {
      toast.error(e.message || "Sorry! That photo couldn't be processed.")
      setIsProcessingPhoto?.(false)
    } finally {
      setBtnActive(true)
    }
  }

  const onRemoveGif = (e) => {
    e.preventDefault()
    setGifFile(null)
    setBtnActive(true)
  }

  const onRemovePhoto = (e) => {
    e.preventDefault()
    hiddenFileInput.current = null
    setRemoveImage(true)
    setBtnActive(true)
  }

  const onRemoveAndRegenerate = (e) => {
    e.preventDefault()
    setCoverRemoved(true)
    if (onRemoveCoverImage) onRemoveCoverImage()
  }

  const onCancel = () => {
    setBtnActive(false)
    hiddenFileInput.current = null
    setUploadedFilePath(null)
    setBtnActive(true)
  }

  const gifPreviewStyle = {
    width: "100%",
    aspectRatio: "16 / 9",
  }

  const onReady = () => {
    const cropper = cropperRef.current?.cropper
    if (!cropper) {
      return
    }
    const containerData = cropper.getContainerData()
    const canvasData = cropper.getCanvasData()
    const imageAspectRatio = canvasData.naturalWidth / canvasData.naturalHeight
    // if the aspectRatio is less than our 16/9 desires, reset the cropBox size to the containerSize
    if (imageAspectRatio < 16 / 9) {
      cropper.setCropBoxData({
        left: 0,
        top: 0,
        width: containerData.width,
        height: containerData.height,
      })
    }
  }

  return (
    <div className="grid grid-cols-12">
      <label className="block col-span-12 text-gray-900 text-xs uppercase font-bold">
        Show Flyer Image
      </label>
      <div className="col-span-12">
        {coverImageUrl && !removeImage && !coverRemoved ? (
          <div>
            <img
              src={coverImageUrl}
              alt="existing flyer"
              className="bg-black w-full"
            />
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                disabled={!btnActive}
                className="text-sm py-1 px-2 rounded-sm border border-gray-500 text-gray-600 focus:outline-none bg-transparent"
                onClick={onRemovePhoto}
              >
                Replace Image
              </button>
              {!isGeneratedCover && (
                <button
                  type="button"
                  disabled={!btnActive}
                  className="text-sm py-1 px-2 rounded-sm border border-red-600 text-red-600 focus:outline-none bg-transparent"
                  onClick={onRemoveAndRegenerate}
                >
                  Remove Image
                </button>
              )}
            </div>
          </div>
        ) : coverRemoved ? (
          <div className="bg-gray-200 h-64 flex items-center justify-center">
            <p className="text-gray-500 text-sm text-center px-4">
              Image removed. A default cover will be generated on save.
            </p>
          </div>
        ) : gifPreview !== null ? (
          <div>
            <img
              src={gifPreview}
              alt="existing flyer"
              style={gifPreviewStyle}
            />
            <Button
              disabled={!btnActive}
              bg="bg-gray-400"
              className="mt-2"
              onClick={onRemoveGif}
            >
              Replace Image
            </Button>
          </div>
        ) : uploadedFilePath && uploadedFilePath.length ? (
          <>
            <h6 className="font-bold">Crop your photo</h6>
            <p className="text-xs text-gray-500">
              {isMobile ? "Pinch" : "Scroll"} to zoom
            </p>
            <Cropper
              className="mt-2"
              style={{ aspectRatio: "16/9", overflow: "hidden", width: "100%" }}
              src={uploadedFilePath}
              viewMode={0}
              initialAspectRatio={16 / 9}
              aspectRatio={16 / 9}
              center={true}
              dragMode="move"
              cropBoxResizable={false}
              cropBoxMovable={false}
              zoomable={true}
              scalable={true}
              zoomOnTouch={true}
              zoomOnWheel={true}
              wheelZoomRatio={0.08}
              background={true}
              modal={true}
              guides={true}
              autoCrop={true}
              autoCropArea={1}
              minCropBoxWidth={0.8}
              restore={false}
              checkCrossOrigin={false}
              toggleDragModeOnDblclick={false}
              checkOrientation={false} // https://github.com/fengyuanchen/cropperjs/issues/671
              ref={cropperRef}
              ready={onReady}
              onInitialized={(instance) => {
                setCropper(instance)
                setIsProcessingPhoto?.(false)
              }}
            />
            <div className="mt-2 mb-2">
              <OutlineButton
                type="button"
                disabled={!btnActive}
                className="text-gray-800"
                onClick={onCancel}
              >
                Remove
              </OutlineButton>
            </div>
          </>
        ) : (
          <>
            <CardImage className="w-full col-span-12 text-gray-500 h-64 block bg-gray-200" />
            <button
              type="button"
              disabled={!btnActive}
              className="mt-2 w-full text-sm py-1 px-2 rounded-sm border border-green-700 text-green-700 focus:outline-none bg-transparent"
              onClick={handleClick}
            >
              Upload an Image
            </button>
            <input
              type="file"
              ref={hiddenFileInput}
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => onUploadFlyerPhoto(e.target.files[0])}
            />
          </>
        )}
      </div>
    </div>
  )
}
